import React, { useEffect } from 'react';
import { ArrowLeft, AlertTriangle, Shield, Target, TrendingUp, Users, FileText, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const RiskManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <AlertTriangle className="service-badge-icon" />
              <span>Risk Management & Assessment</span>
            </div>
            
            <h1 className="service-title">
              Strategic Risk Evaluation & 
              <span className="service-title-accent"> Mitigation Planning</span>
            </h1>
            
            <p className="service-description">
              Comprehensive risk assessment services that align cybersecurity investments with business 
              objectives, ensuring optimal resource allocation and measurable security improvements.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">95%</span>
                <span className="service-stat-label">Risk Reduction Achievement</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">40%</span>
                <span className="service-stat-label">Security Budget Optimization</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">100%</span>
                <span className="service-stat-label">Regulatory Compliance Rate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Framework Section */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">NIST-Aligned Risk Framework</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Identify</h3>
                <p className="body-medium">
                  Comprehensive asset discovery, business process mapping, and threat landscape analysis 
                  to establish a complete understanding of your organization's risk profile.
                </p>
                <ul className="step-features">
                  <li>Asset inventory and classification</li>
                  <li>Business impact analysis</li>
                  <li>Supply chain risk assessment</li>
                  <li>Regulatory requirement mapping</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Protect</h3>
                <p className="body-medium">
                  Design and implement comprehensive security controls based on risk priorities, 
                  industry best practices, and regulatory requirements specific to your sector.
                </p>
                <ul className="step-features">
                  <li>Control framework design</li>
                  <li>Security architecture review</li>
                  <li>Policy development and alignment</li>
                  <li>Staff training and awareness</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">Detect</h3>
                <p className="body-medium">
                  Implement continuous monitoring capabilities with advanced detection mechanisms 
                  to identify potential security events and anomalous activities in real-time.
                </p>
                <ul className="step-features">
                  <li>SIEM/SOAR implementation</li>
                  <li>Threat hunting programs</li>
                  <li>Anomaly detection systems</li>
                  <li>Security metrics and KPIs</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Respond & Recover</h3>
                <p className="body-medium">
                  Develop comprehensive incident response and business continuity plans with regular 
                  testing and continuous improvement based on lessons learned.
                </p>
                <ul className="step-features">
                  <li>Incident response planning</li>
                  <li>Business continuity design</li>
                  <li>Crisis communication plans</li>
                  <li>Recovery time optimization</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Assessment Types */}
      <section className="assessment-types-section">
        <div className="dark-content-container">
          <h2 className="display-large">Comprehensive Assessment Services</h2>
          
          <div className="assessment-grid">
            <div className="assessment-card">
              <Shield className="assessment-icon" />
              <h3 className="heading-2">Quantitative Risk Assessment</h3>
              <p className="body-medium">
                Financial impact analysis using FAIR methodology to quantify cyber risk in monetary terms, 
                enabling data-driven investment decisions and board-level reporting.
              </p>
              <ul className="assessment-features">
                <li>Monte Carlo simulations</li>
                <li>Loss event frequency analysis</li>
                <li>Financial impact modeling</li>
                <li>ROI calculations for security investments</li>
              </ul>
            </div>

            <div className="assessment-card">
              <Target className="assessment-icon" />
              <h3 className="heading-2">Qualitative Risk Assessment</h3>
              <p className="body-medium">
                Expert-driven risk evaluation focusing on threat scenarios, control effectiveness, 
                and business impact assessment using industry-standard risk rating methodologies.
              </p>
              <ul className="assessment-features">
                <li>Threat modeling workshops</li>
                <li>Control gap analysis</li>
                <li>Risk heat mapping</li>
                <li>Scenario-based assessments</li>
              </ul>
            </div>

            <div className="assessment-card">
              <Users className="assessment-icon" />
              <h3 className="heading-2">Third-Party Risk Assessment</h3>
              <p className="body-medium">
                Comprehensive vendor and supply chain security evaluations to identify and mitigate 
                risks from external partners and service providers.
              </p>
              <ul className="assessment-features">
                <li>Vendor security questionnaires</li>
                <li>On-site security audits</li>
                <li>Contract security reviews</li>
                <li>Continuous monitoring programs</li>
              </ul>
            </div>

            <div className="assessment-card">
              <FileText className="assessment-icon" />
              <h3 className="heading-2">Regulatory Risk Assessment</h3>
              <p className="body-medium">
                Compliance gap analysis and regulatory risk evaluation for GDPR, HIPAA, SOX, 
                PCI DSS, and other industry-specific requirements.
              </p>
              <ul className="assessment-features">
                <li>Compliance gap analysis</li>
                <li>Regulatory mapping</li>
                <li>Audit readiness preparation</li>
                <li>Remediation roadmaps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Risk Reporting */}
      <section className="reporting-section">
        <div className="dark-content-container">
          <h2 className="display-large">Executive Risk Reporting & Metrics</h2>
          
          <div className="reporting-grid">
            <div className="reporting-content">
              <h3 className="heading-2">Board-Ready Risk Communications</h3>
              <p className="body-large">
                Transform complex technical risks into clear, actionable business intelligence 
                that enables informed decision-making at the executive level.
              </p>
              
              <div className="reporting-features">
                <div className="reporting-feature">
                  <BarChart3 className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Risk Dashboards</h4>
                    <p className="body-small">Real-time risk metrics with trend analysis and KPI tracking</p>
                  </div>
                </div>
                
                <div className="reporting-feature">
                  <TrendingUp className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Business Impact Analysis</h4>
                    <p className="body-small">Financial modeling of potential loss scenarios and mitigation ROI</p>
                  </div>
                </div>
                
                <div className="reporting-feature">
                  <FileText className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Regulatory Reporting</h4>
                    <p className="body-small">Automated compliance reporting for audit and regulatory requirements</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="reporting-image">
              <img 
                src="https://images.unsplash.com/photo-1654663477425-acf704a970d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHx3aXphcmQlMjBsb2dvfGVufDB8fHx8MTc1ODgyMjY4MHww&ixlib=rb-4.1.0&q=85"
                alt="Risk Analytics Dashboard"
                className="reporting-img"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RiskManagementPage;